import { useQuery } from '@apollo/client'
import { useTranslation } from 'next-i18next'
import { useFormContext } from 'react-hook-form'
import { useMemo } from 'react'
import ShowroomInfo from 'modules/checkout/components/ShowroomInfo'
import Select from '_app/components/Form/Select'
import {
  CartAddressInput,
  Maybe,
  Showroom,
  Showrooms,
} from '_app/graphql/generated/graphql'
import { GET_SHOWROOMS } from '_app/graphql/queries/content/GetShowrooms'
import { Option } from '_app/types/form'
import { parseShowroomAddress } from './utils'

type ShowroomWithId = Omit<Showroom, 'id' | 'name'> & {
  id: string;
  name: string;
};

const foreignShowroomId = {
  Dusseldorf: '35',
  Munster: '37',
  Paris: '41',
}

const foreignIds = Object.values(foreignShowroomId)

interface Props {
  onChange: (address: Partial<CartAddressInput>) => void;
}

export const ShowroomsSelect = ({ onChange }: Props) => {
  const { t } = useTranslation()
  const { data } = useQuery<{ showrooms: Maybe<Showrooms>}>(GET_SHOWROOMS, {
    variables: {
      isPickupOnly: true
    }
  })
  const { watch } = useFormContext()

  const showrooms = (data?.showrooms?.items || [])
    .filter(
      (showroom): showroom is ShowroomWithId =>
        !!showroom?.id && !!showroom.name && !foreignIds.includes(showroom.id)
    )
    .sort((a, b) => a.name.localeCompare(b.name))

  const showroomTarget = watch('showroom')
  const selectedShowroom = useMemo(
    () => showrooms.find(showroomItem => showroomItem.id === showroomTarget),
    [showrooms, showroomTarget]
  )

  const handleChange = (showroom: Option<string>) => {
    const selected = showrooms.find(
      showroomItem => showroomItem.id === showroom.value
    )
    if (!selected) return

    const address = parseShowroomAddress(selected)
    onChange({
      country_code: 'pl',
      ...address,
    })
  }

  return (
    <>
      <Select
        isRequired
        label={t('CHOOSE_BOUTIQUE')}
        name="showroom"
        setActive={handleChange}
        options={showrooms.map(option => ({
          label: option.name,
          value: option.id,
        }))}
      />
      {selectedShowroom && <ShowroomInfo showroom={selectedShowroom} />}
    </>
  )
}
